import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useApp } from '../../context/AppContext'
import { api } from '../../services/api'
import { 
  Menu, 
  X, 
  User, 
  LogOut, 
  Camera, 
  FileText,
  Heart,
  Settings
} from 'lucide-react'

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const { isAuthenticated, user, logout } = useApp()
  const navigate = useNavigate()

  useEffect(() => {
    if (isAuthenticated) {
      checkAdminStatus()
    }
  }, [isAuthenticated])

  const checkAdminStatus = async () => {
    if (!isAuthenticated) {
      setIsAdmin(false)
      return
    }
    
    try {
      await api.get('/admin/stats')
      setIsAdmin(true)
    } catch (error) {
      setIsAdmin(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/')
    setIsProfileOpen(false)
  }

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-neutral-200/50 shadow-soft">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              <img 
                src="https://callmedilife-mail-images.s3.ap-south-1.amazonaws.com/cml-logo-new.png"
                alt="FaceScan AI Logo"
                className="w-[10rem] h-[10rem] object-contain group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <Link 
              to="/" 
              className="px-4 py-2 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200 font-medium"
            >
              Home
            </Link>
            
            {isAuthenticated && (
              <>
                <Link 
                  to="/scan" 
                  className="flex items-center px-4 py-2 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200 font-medium"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Scan
                </Link>
                <Link 
                  to="/history" 
                  className="flex items-center px-4 py-2 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200 font-medium"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  History
                </Link>
                <Link 
                  to="/hra" 
                  className="flex items-center px-4 py-2 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200 font-medium"
                >
                  <Heart className="w-4 h-4 mr-2" />
                  Health Risk
                </Link>
                {isAdmin && (
                  <Link 
                    to="/admin" 
                    className="flex items-center px-4 py-2 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200 font-medium"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Admin
                  </Link>
                )}
              </>
            )}
          </nav>

          {/* User Menu */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center space-x-3 px-4 py-2 bg-gradient-to-r from-primary-50 to-secondary-50 hover:from-primary-100 hover:to-secondary-100 rounded-xl transition-all duration-200 border border-primary-200/50"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 text-white rounded-lg flex items-center justify-center shadow-sm">
                    <User className="w-4 h-4" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-neutral-900">
                      {user?.name || 'User'}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {user?.phone_number}
                    </p>
                  </div>
                </button>

                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white/90 backdrop-blur-md rounded-2xl shadow-large border border-neutral-200/50 py-2 z-50 animate-slide-down">
                    <div className="px-4 py-3 border-b border-neutral-100">
                      <p className="text-sm font-semibold text-neutral-900">
                        {user?.name || 'User'}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {user?.phone_number}
                      </p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link 
                to="/scan" 
                className="btn-primary"
              >
                Get Started
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-neutral-200/50 py-4 animate-slide-down">
            <nav className="flex flex-col space-y-2">
              <Link 
                to="/" 
                className="px-4 py-3 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              
              {isAuthenticated && (
                <>
                  <Link 
                    to="/scan" 
                    className="flex items-center px-4 py-3 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200 font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Camera className="w-4 h-4 mr-3" />
                    Scan
                  </Link>
                  <Link 
                    to="/history" 
                    className="flex items-center px-4 py-3 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200 font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FileText className="w-4 h-4 mr-3" />
                    History
                  </Link>
                  <Link 
                    to="/hra" 
                    className="flex items-center px-4 py-3 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200 font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Heart className="w-4 h-4 mr-3" />
                    Health Risk
                  </Link>
                  {isAdmin && (
                    <Link 
                      to="/admin" 
                      className="flex items-center px-4 py-3 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200 font-medium"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Settings className="w-4 h-4 mr-3" />
                      Admin
                    </Link>
                  )}
                </>
              )}

              <div className="border-t border-neutral-200/50 pt-4 mt-4">
                {isAuthenticated ? (
                  <div className="space-y-2">
                    <div className="px-4 py-2 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl">
                      <p className="text-sm font-semibold text-neutral-900">
                        {user?.name || 'User'}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {user?.phone_number}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        handleLogout()
                        setIsMenuOpen(false)
                      }}
                      className="flex items-center w-full px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 rounded-xl transition-colors"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                ) : (
                  <Link 
                    to="/scan" 
                    className="btn-primary w-full text-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}

export default Header
